import { useRef } from "react";
import { Icon } from "../landing/icons";
import { ANSWERS, AREAS, type LiteArea } from "./areas";

interface GeneratorProps {
  openCard: string | null;
  cardAnswers: Record<string, string>;
  unansweredCards: string[];
  isValidating: boolean;
  onToggle: (key: string) => void;
  onAnswer: (key: string, value: string) => void;
  onGenerate: () => void;
}

/**
 * The ReImagine Lite form. Same behaviour as before: nine areas, one Yes / No / Maybe answer each, the next
 * area opens after an answer, and "Generate" validates then hands over to the details dialog.
 *
 * The card stays one size: the area tiles and the question view share one spot, so opening an area swaps the
 * tiles for its question instead of growing the card.
 */
export default function Generator({
  openCard,
  cardAnswers,
  unansweredCards,
  isValidating,
  onToggle,
  onAnswer,
  onGenerate,
}: GeneratorProps) {
  const answered = AREAS.filter((a) => cardAnswers[a.key] !== "").length;
  const open = AREAS.find((a) => a.key === openCard) ?? null;

  // Keep rendering the last open area while the question view fades out.
  const lastOpen = useRef<LiteArea>(AREAS[0]);
  if (open) lastOpen.current = open;
  const shown = open ?? lastOpen.current;
  const shownIndex = AREAS.findIndex((a) => a.key === shown.key);

  return (
    <form
      className="rl-gen"
      aria-labelledby="rl-gen-h"
      onSubmit={(e) => {
        e.preventDefault();
        onGenerate();
      }}
    >
      <div className="rl-gen__head">
        <b>
          <i>
            <Icon name="map" size={16} />
          </i>
          Roadmap generator
        </b>
        <small>Step 1 of 3</small>
      </div>

      <div className="rl-gen__body">
        <h2 className="rl-gen__q" id="rl-gen-h">
          Where does your organization stand in each area?
        </h2>

        <div className="rl-stage">
          <ul className={`rl-tiles${open ? " is-hidden" : ""}`} aria-hidden={open ? true : undefined}>
            {AREAS.map((area) => {
              const answer = cardAnswers[area.key];
              const done = answer !== "";
              const missing = unansweredCards.includes(area.key);
              return (
                <li key={area.key}>
                  <button
                    type="button"
                    className={`rl-tile${done ? " is-done" : ""}${missing ? " is-missing" : ""}`}
                    onClick={() => onToggle(area.key)}
                    tabIndex={open ? -1 : 0}
                  >
                    {/* Answered tiles show Y / N / M in the badge; the full answer is read out below. */}
                    <span className="rl-tile__ck" aria-hidden="true">
                      {done ? answer.charAt(0) : ""}
                    </span>
                    <span className="rl-tile__ico">
                      <Icon name={area.icon} />
                    </span>
                    <span className="rl-tile__t">{area.title}</span>
                    {done && <span className="rl-sr">Answered {answer}</span>}
                    {missing && <span className="rl-sr">Needs an answer</span>}
                  </button>
                </li>
              );
            })}
          </ul>

          <div
            className={`rl-qv${open ? "" : " is-hidden"}`}
            id="rl-qpanel"
            role="group"
            aria-label="Question"
            aria-live="polite"
          >
            <div className="rl-qv__top">
              <button
                type="button"
                className="rl-qv__back"
                tabIndex={open ? 0 : -1}
                onClick={() => open && onToggle(open.key)}
              >
                <Icon name="back" size={16} />
                All areas
              </button>
              <span className="rl-qv__n">
                {shownIndex + 1} of {AREAS.length}
              </span>
            </div>

            <div className="rl-qv__main">
              <span className="rl-qp__k">{shown.title}</span>
              <p className="rl-qv__text">{shown.question}</p>
              <div className="rl-qp__opts" role="radiogroup" aria-label={shown.title}>
                {ANSWERS.map((value) => (
                  <label className="rl-opt" key={value}>
                    <input
                      type="radio"
                      name={`${shown.key}-answer`}
                      value={value}
                      checked={cardAnswers[shown.key] === value}
                      tabIndex={open ? 0 : -1}
                      onChange={(e) => onAnswer(shown.key, e.target.value)}
                    />
                    <span>{value}</span>
                  </label>
                ))}
              </div>
            </div>

            <ol className="rl-qv__dots" aria-label="Jump to an area">
              {AREAS.map((area, i) => {
                const done = cardAnswers[area.key] !== "";
                const missing = unansweredCards.includes(area.key);
                const current = area.key === shown.key;
                return (
                  <li key={area.key}>
                    <button
                      type="button"
                      className={`rl-dotbtn${done ? " is-done" : ""}${current ? " is-current" : ""}${missing ? " is-missing" : ""}`}
                      aria-label={`${area.title}${done ? `, answered ${cardAnswers[area.key]}` : ""}`}
                      aria-current={current ? "step" : undefined}
                      tabIndex={open ? 0 : -1}
                      onClick={() => !current && onToggle(area.key)}
                    >
                      {i + 1}
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="rl-gen__foot">
          <small className="rl-cnt">
            <b>{answered}</b> of {AREAS.length} areas answered
          </small>
          <button type="submit" className="rl-btn rl-btn--primary" disabled={isValidating}>
            {isValidating && <span className="rl-spin-dot" aria-hidden="true" />}
            {isValidating ? "Validating..." : "Generate data + AI roadmap"}
            {!isValidating && <Icon name="arrow" size={18} />}
          </button>
        </div>
      </div>
    </form>
  );
}
